import {
  Button,
  Checkbox,
  DatePicker,
  Input,
  Modal,
  Popconfirm,
  Radio,
  Rate,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Tooltip,
} from "antd";
import { AntPageContainer } from "../../shared/components/AntPageContainer";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EyeOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import surveyApi, {
  SurveyDetail,
  SurveyResponseItem,
  SurveyQuestion,
  SurveySection,
  QuestionType,
  MultilingualText,
  QuestionOption,
} from "../../survey/api";
import useAlert from "../../shared/hooks/useAlert";
import dayjs from "dayjs";

const LANGUAGE_LABELS: Record<string, string> = {
  en: "English",
  fr: "Français",
  ko: "한국인",
  vi: "Tiếng Việt",
  zh: "中文",
  th: "แบบไทย",
  es: "Español",
  ru: "Русский",
  hi: "हिन्दी",
};

const getLocalizedTitle = (
  title: MultilingualText | undefined | null,
  fallback: string = "Untitled"
): string => {
  if (!title) return fallback;
  return (
    title.en ||
    title.fr ||
    title.ko ||
    title.vi ||
    title.zh ||
    title.th ||
    title.es ||
    title.ru ||
    title.hi ||
    fallback
  );
};

const getLocalizedText = (
  text: MultilingualText | undefined,
  lang: string = "en"
): string => {
  if (!text) return "";
  return (
    (text as Record<string, string>)[lang] ||
    text.en ||
    text.fr ||
    text.ko ||
    text.vi ||
    text.zh ||
    text.th ||
    text.es ||
    text.ru ||
    text.hi ||
    ""
  );
};

// Check if a question is conditional (depends on another question's answer)
const isConditionalQuestion = (question: SurveyQuestion): boolean => {
  return !!(
    question.conditionQuestionId && question.conditionOptionIds?.length
  );
};

// View Response Modal Component
interface ViewResponseModalProps {
  open: boolean;
  onClose: () => void;
  survey: SurveyDetail | null;
  response: SurveyResponseItem | null;
}

const ViewResponseModal: React.FC<ViewResponseModalProps> = ({
  open,
  onClose,
  survey,
  response,
}) => {
  if (!survey || !response) return null;

  // Get all questions organized by sections
  const getAllQuestions = () => {
    const questionsWithoutSection = survey.questions.filter(
      (q) => !q.sectionId
    );
    return { sections: survey.sections || [], questionsWithoutSection };
  };

  const { sections, questionsWithoutSection } = getAllQuestions();

  const getAnswerValue = (questionId: number) => {
    return response.answers.find((a) => a.questionId === questionId);
  };

  const renderAnswer = (question: SurveyQuestion) => {
    const answer = getAnswerValue(question.id!);
    if (!answer) {
      return <span className="text-gray-400 italic">No answer</span>;
    }

    switch (question.type) {
      case QuestionType.SHORT_TEXT: {
        // Check if the answer contains multiple values (JSON array)
        let textValues: string[] = [];
        try {
          const parsed = JSON.parse(answer.textValue || "");
          if (Array.isArray(parsed)) {
            textValues = parsed;
          }
        } catch {
          // Not JSON, treat as single value
        }

        if (textValues.length > 1) {
          // Multiple answer boxes
          return (
            <div className="space-y-2">
              {textValues.map((value, index) => (
                <div key={index} className="flex items-center gap-2">
                  <span className="text-gray-500 text-sm min-w-[20px]">
                    {index + 1}.
                  </span>
                  <Input value={value} readOnly className="bg-gray-50 flex-1" />
                </div>
              ))}
            </div>
          );
        }

        // Single answer box
        return (
          <Input
            value={answer.textValue || ""}
            readOnly
            className="bg-gray-50"
          />
        );
      }
      case QuestionType.LONG_TEXT:
        return (
          <Input.TextArea
            value={answer.textValue || ""}
            readOnly
            rows={3}
            className="bg-gray-50"
          />
        );
      case QuestionType.DATE:
        return (
          <DatePicker
            value={answer.dateValue ? dayjs(answer.dateValue) : null}
            disabled
            className="w-full"
          />
        );
      case QuestionType.MULTIPLE_CHOICE: {
        const selectedOption = question.options.find(
          (o) => o.id === answer.selectedOptionId
        );
        return (
          <div className="response-readonly-view">
            <Radio.Group value={answer.selectedOptionId} disabled>
              <div className="flex flex-col gap-2">
                {question.options
                  .sort((a, b) => a.order - b.order)
                  .map((option: QuestionOption) => (
                    <Radio key={option.id} value={option.id}>
                      {getLocalizedText(option.text)}
                    </Radio>
                  ))}
              </div>
            </Radio.Group>
            {/* Show "Other" text value if an "Other" option was selected */}
            {selectedOption?.isOtherOption && answer.textValue && (
              <div className="mt-2 ml-6">
                <span className="text-gray-500 text-sm">Specified: </span>
                <span className="text-gray-800">{answer.textValue}</span>
              </div>
            )}
          </div>
        );
      }
      case QuestionType.MULTI_SELECT: {
        const otherOption = question.options.find((o) => o.isOtherOption);
        const isOtherSelected =
          otherOption &&
          answer.selectedOptionIds?.includes(otherOption.id as number);
        return (
          <div className="response-readonly-view">
            <Checkbox.Group value={answer.selectedOptionIds || []} disabled>
              <div className="flex flex-col gap-2">
                {question.options
                  .sort((a, b) => a.order - b.order)
                  .map((option: QuestionOption) => (
                    <Checkbox key={option.id} value={option.id}>
                      {getLocalizedText(option.text)}
                    </Checkbox>
                  ))}
              </div>
            </Checkbox.Group>
            {/* Show "Other" text value if an "Other" option was selected */}
            {isOtherSelected && answer.textValue && (
              <div className="mt-2 ml-6">
                <span className="text-gray-500 text-sm">Specified: </span>
                <span className="text-gray-800">{answer.textValue}</span>
              </div>
            )}
          </div>
        );
      }
      case QuestionType.RATING:
        return (
          <div className="response-readonly-view flex items-center gap-3">
            <Rate count={10} value={answer.ratingValue || 0} disabled />
            <span className="text-gray-600">
              {answer.ratingValue ? `(${answer.ratingValue}/10)` : ""}
            </span>
          </div>
        );
      default:
        return <span className="text-gray-400">Unknown question type</span>;
    }
  };

  const renderQuestionBlock = (
    question: SurveyQuestion,
    index: number | null
  ) => (
    <div key={question.id} className="mb-6 p-4 bg-gray-50 rounded-lg">
      <div className="flex items-start gap-2 mb-3">
        {index !== null && (
          <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm font-medium">
            Q{index}
          </span>
        )}
        <div className="flex-1">
          <h4 className="font-medium text-gray-800 m-0">
            {getLocalizedTitle(question.title)}
          </h4>
          <span className="text-xs text-gray-500 mt-1">
            {question.type === QuestionType.SHORT_TEXT && "Short Text"}
            {question.type === QuestionType.LONG_TEXT && "Long Text"}
            {question.type === QuestionType.DATE && "Date"}
            {question.type === QuestionType.MULTIPLE_CHOICE &&
              "Multiple Choice"}
            {question.type === QuestionType.MULTI_SELECT && "Multi Select"}
            {question.type === QuestionType.RATING && "Rating"}
            {question.isRequired && " • Required"}
          </span>
        </div>
      </div>
      <div className={index !== null ? "ml-8" : ""}>
        {renderAnswer(question)}
      </div>
    </div>
  );

  let questionCounter = 0;

  return (
    <Modal
      title={
        <div>
          <h3 className="text-lg font-semibold m-0">Response Details</h3>
          <p className="text-sm text-gray-500 m-0 mt-1">
            Submitted on{" "}
            {new Intl.DateTimeFormat("en-GB", {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            }).format(new Date(response.submittedAt))}
            {" • "}
            <Tag color="blue">
              {LANGUAGE_LABELS[response.language] || response.language}
            </Tag>
          </p>
        </div>
      }
      open={open}
      onCancel={onClose}
      width={700}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
      ]}
    >
      <div className="max-h-[60vh] overflow-y-auto">
        {/* Render sections with their questions */}
        {sections
          .sort((a, b) => a.order - b.order)
          .map((section: SurveySection, sectionIndex: number) => (
            <div key={section.id} className="mb-6">
              {/* Only show section header if title has content */}
              {getLocalizedTitle(section.title, "") ? (
                <h3 className="text-lg font-semibold border-b pb-2 mb-4">
                  {getLocalizedTitle(section.title)}
                </h3>
              ) : sections.length > 1 ? (
                <h3 className="text-lg font-semibold border-b pb-2 mb-4 text-gray-400">
                  Section {sectionIndex + 1}
                </h3>
              ) : null}
              {section.questions
                .sort((a, b) => a.order - b.order)
                .map((question) => {
                  // Only increment counter for non-conditional questions
                  const isConditional = isConditionalQuestion(question);
                  const displayNumber = isConditional
                    ? null
                    : ++questionCounter;
                  return renderQuestionBlock(question, displayNumber);
                })}
            </div>
          ))}

        {/* Render legacy questions (not in a section) */}
        {questionsWithoutSection.length > 0 && (
          <div>
            {sections.length > 0 && (
              <h3 className="text-lg font-semibold border-b pb-2 mb-4">
                Other Questions
              </h3>
            )}
            {questionsWithoutSection
              .sort((a, b) => a.order - b.order)
              .map((question) => {
                // Only increment counter for non-conditional questions
                const isConditional = isConditionalQuestion(question);
                const displayNumber = isConditional ? null : ++questionCounter;
                return renderQuestionBlock(question, displayNumber);
              })}
          </div>
        )}
      </div>
    </Modal>
  );
};

const PAGE_SIZE = 15;

export const SurveyResponses = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { setAlert } = useAlert();
  const [survey, setSurvey] = useState<SurveyDetail | null>(null);
  const [surveyLoading, setSurveyLoading] = useState(true);
  const [responses, setResponses] = useState<SurveyResponseItem[]>([]);
  const [totalResponses, setTotalResponses] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedResponse, setSelectedResponse] =
    useState<SurveyResponseItem | null>(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState<string | undefined>(
    undefined
  );

  // Fetch survey details (only once)
  const fetchSurvey = useCallback(async () => {
    if (!id) return;
    try {
      setSurveyLoading(true);
      const surveyRes = await surveyApi.getById(Number(id));
      setSurvey(surveyRes.data ?? null);
    } catch (error) {
      console.error("Error fetching survey:", error);
      setAlert("Error fetching survey", "error");
    } finally {
      setSurveyLoading(false);
    }
  }, [id, setAlert]);

  // Fetch responses with pagination and channel filter
  const fetchResponses = useCallback(
    async (page: number, channel?: string) => {
      if (!id) return;
      try {
        setLoading(true);
        const responsesRes = await surveyApi.getResponses(
          Number(id),
          page,
          PAGE_SIZE,
          channel
        );
        setResponses(responsesRes.data?.items ?? []);
        setTotalResponses(responsesRes.data?.total ?? 0);
      } catch (error) {
        console.error("Error fetching responses:", error);
        setAlert("Error fetching survey responses", "error");
      } finally {
        setLoading(false);
      }
    },
    [id, setAlert]
  );

  // Initial load
  useEffect(() => {
    fetchSurvey();
  }, [fetchSurvey]);

  // Fetch responses when page or channel changes
  useEffect(() => {
    fetchResponses(currentPage, selectedChannel);
  }, [currentPage, selectedChannel, fetchResponses]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const fetchData = useCallback(() => {
    fetchResponses(currentPage, selectedChannel);
  }, [currentPage, selectedChannel, fetchResponses]);

  // Handle channel tab change
  const handleChannelChange = (channel: string) => {
    setSelectedChannel(channel === "all" ? undefined : channel);
    setCurrentPage(1); // Reset to first page when changing channel
  };

  const handleDelete = async (responseId: number) => {
    try {
      await surveyApi.deleteResponse(responseId);
      setAlert("Response deleted successfully", "success");
      fetchData();
    } catch (error) {
      console.error("Error deleting response:", error);
      setAlert("Error deleting response", "error");
    }
  };

  const handleExportExcel = async () => {
    if (!survey) return;
    try {
      setExportLoading(true);
      await surveyApi.exportXLSX(
        Number(id),
        getLocalizedTitle(survey.title, survey.publicId),
        selectedChannel
      );
      const channelMsg = selectedChannel
        ? ` for channel "${selectedChannel}"`
        : "";
      setAlert(`Excel file exported successfully${channelMsg}`, "success");
    } catch (error) {
      console.error("Error exporting Excel:", error);
      setAlert("Error exporting Excel file", "error");
    } finally {
      setExportLoading(false);
    }
  };

  const handleViewResponse = (response: SurveyResponseItem) => {
    setSelectedResponse(response);
    setViewModalOpen(true);
  };

  if (surveyLoading) {
    return (
      <AntPageContainer>
        <div className="flex items-center justify-center min-h-[400px]">
          <Spin size="large" />
        </div>
      </AntPageContainer>
    );
  }

  if (!survey) {
    return (
      <AntPageContainer>
        <div className="text-center py-10">
          <h2 className="text-xl mb-4">Survey not found</h2>
          <Button onClick={() => navigate("/admin/surveys")}>
            Back to Surveys
          </Button>
        </div>
      </AntPageContainer>
    );
  }

  // Count total questions for display
  const getTotalQuestions = () => {
    let count = 0;
    if (survey.sections) {
      survey.sections.forEach((s) => {
        count += s.questions?.length || 0;
      });
    }
    count += survey.questions.filter((q) => !q.sectionId).length;
    return count;
  };

  const columns = [
    {
      key: "submittedAt",
      title: "Submitted At",
      dataIndex: "submittedAt",
      width: 180,
      render: (_: unknown, record: SurveyResponseItem) => {
        const date = new Date(record.submittedAt);
        return new Intl.DateTimeFormat("en-GB", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        }).format(date);
      },
    },
    {
      key: "language",
      title: "Language",
      dataIndex: "language",
      width: 120,
      render: (_: unknown, record: SurveyResponseItem) => (
        <Tag color="blue">
          {LANGUAGE_LABELS[record.language] || record.language}
        </Tag>
      ),
    },
    {
      key: "channel",
      title: "Channel",
      dataIndex: "channel",
      width: 120,
      render: (_: unknown, record: SurveyResponseItem) =>
        record.channel ? (
          <Tag color="green">{record.channel}</Tag>
        ) : (
          <span className="text-gray-400">-</span>
        ),
    },
    {
      key: "answers",
      title: "Answers",
      width: 120,
      render: (_: unknown, record: SurveyResponseItem) => (
        <Tag color="green">
          {record.answers.length} / {getTotalQuestions()}
        </Tag>
      ),
    },
    {
      key: "actions",
      title: "Actions",
      width: 120,
      fixed: "right" as const,
      render: (_: unknown, record: SurveyResponseItem) => (
        <Space>
          <Tooltip title="View Response">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewResponse(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete Response"
            description="Are you sure you want to delete this response? This action will soft-delete the response."
            onConfirm={() => handleDelete(record.id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
          >
            <Tooltip title="Delete">
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <AntPageContainer>
      <style>{`
        .response-readonly-view .ant-radio-wrapper-disabled,
        .response-readonly-view .ant-checkbox-wrapper-disabled,
        .response-readonly-view .ant-checkbox-wrapper-disabled span,
        .response-readonly-view .ant-rate-disabled {
          color: rgba(0, 0, 0, 0.88);
          cursor: default;
        }
        .response-readonly-view .ant-radio-wrapper-disabled .ant-radio-checked .ant-radio-inner {
          border-color: #1677ff;
          background-color: #1677ff38;
        }
        .response-readonly-view .ant-checkbox-wrapper-disabled .ant-checkbox-checked .ant-checkbox-inner {
          border-color: #1677ff;
          background-color: #1677ff38;
        }
      `}</style>
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate("/admin/surveys")}
            >
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold m-0">
                {getLocalizedTitle(survey.title)}
              </h1>
              <p className="text-gray-500 m-0">Survey Responses</p>
            </div>
          </div>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchData}>
              Refresh
            </Button>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleExportExcel}
              loading={exportLoading}
            >
              Export Excel
            </Button>
          </Space>
        </div>
      </div>

      {/* Channel Filter Tabs */}
      {survey.channels && survey.channels.length > 0 && (
        <Tabs
          activeKey={selectedChannel || "all"}
          onChange={handleChannelChange}
          className="mb-4"
          items={[
            {
              key: "all",
              label: "All Channels",
            },
            ...survey.channels.map((ch) => ({
              key: ch,
              label: ch,
            })),
          ]}
        />
      )}

      <Table
        dataSource={responses}
        columns={columns}
        rowKey="id"
        loading={loading}
        scroll={{ x: "max-content" }}
        pagination={{
          current: currentPage,
          pageSize: PAGE_SIZE,
          total: totalResponses,
          showTotal: (total) => `Total Responses: ${total}`,
          showSizeChanger: false,
          onChange: handlePageChange,
        }}
      />

      {/* View Response Modal */}
      <ViewResponseModal
        open={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false);
          setSelectedResponse(null);
        }}
        survey={survey}
        response={selectedResponse}
      />
    </AntPageContainer>
  );
};
